import { useId } from '@react-aria/utils'
import React from 'react'
import type {
  InputValue,
  NumberInputLabelApi,
  NumberInputLabelConfig,
  UseNumberFormatConfig,
} from './NumberInput.types'
import {
  defaultLocale,
  format,
  getLengthInfo,
  verifyDecimalScale,
} from './NumberInput.utils'

export function usePrevious<T>(value: T) {
  const ref = React.useRef<T>()
  React.useEffect(() => {
    ref.current = value
  })
  return ref.current as T
}

export function useNumberFormat({
  decimalScale: _decimalScale = true,
  fillDecimalScale = 'none',
  locale: _locale = defaultLocale,
}: UseNumberFormatConfig) {
  const locale = React.useMemo(
    () =>
      [
        ...(Array.isArray(_locale) ? _locale : [_locale]),
        defaultLocale,
      ] as string[],
    [_locale]
  )

  const { fixedDecimalScale, fillDecimalScaleOnBlur } = React.useMemo(
    () => ({
      fixedDecimalScale: fillDecimalScale === 'always',
      fillDecimalScaleOnBlur: fillDecimalScale === 'onBlur',
    }),
    [fillDecimalScale]
  )

  const decimalScale = React.useMemo(
    () => verifyDecimalScale(_decimalScale),
    [_decimalScale]
  )

  const formatValue = React.useCallback(
    (value: InputValue) =>
      format(locale, decimalScale, fillDecimalScale, value),
    [locale, decimalScale, fillDecimalScale]
  )

  const getValueLengthInfo = React.useCallback(
    (value: InputValue, isNumericString = false) =>
      getLengthInfo(
        locale,
        value,
        decimalScale,
        fillDecimalScale,
        isNumericString
      ),
    [locale, decimalScale, fillDecimalScale]
  )

  return {
    formatValue,
    getValueLengthInfo,
    locale,
    fixedDecimalScale,
    fillDecimalScaleOnBlur,
    decimalScale,
  }
}

export function useNumberInputLabel({
  ariaLabel,
  ariaLabelledBy,
  id,
  name,
  prefix,
  suffix,
}: NumberInputLabelConfig): NumberInputLabelApi {
  const prefixDefaultId = useId()
  const suffixDefaultId = useId()
  let ariaLabelledByChained
  let prefixId
  let suffixId

  if (prefix) {
    prefixId = id ? `${id}-prefix` : name ? `${name}-prefix` : prefixDefaultId
  }
  if (suffix) {
    suffixId = id ? `${id}-suffix` : name ? `${name}-suffix` : suffixDefaultId
  }
  if (ariaLabelledBy) {
    ariaLabelledByChained =
      prefixId || suffixId
        ? `${ariaLabelledBy} ${prefixId || suffixId}`
        : ariaLabelledBy
  }
  if (ariaLabel) {
    ariaLabelledByChained =
      prefixId || suffixId ? `${id} ${prefixId || suffixId}` : id
  }

  return {
    inputProps: {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledByChained,
    },
    prefixProps: {
      'aria-hidden': !ariaLabel,
      id: prefixId,
    },
    suffixProps: {
      'aria-hidden': !ariaLabel,
      id: suffixId,
    },
  }
}
